import { Navigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtTs } from './AlphaOmegaDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

export interface WorkerAllocationResponse {
  allocation_id: string;
  company_id: string;
  worker_delta: number;
  total_after: number;
  reason: string | null;
  allocated_at: number;
  status: string;
  alpha_omega_signature: string | null;
}

interface WorkerAllocationsResponse {
  allocations: WorkerAllocationResponse[];
  total: number;
}

interface ActionResponse {
  ok: boolean;
  id: string;
  action: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtDelta(delta: number): string {
  if (delta > 0) return `+${delta}`;
  return String(delta);
}

export function deltaColor(delta: number): string {
  if (delta > 0) return '#34d399';
  if (delta < 0) return '#f87171';
  return 'var(--omega-ink)';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: ok ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
      border: `1px solid ${ok ? '#34d399' : '#f87171'}`,
      borderRadius: 6, color: ok ? '#34d399' : '#f87171',
      fontFamily: 'var(--omega-mono)', fontSize: '9pt',
      padding: '10px 20px', zIndex: 9999, direction: 'ltr',
    }}>
      {msg}
    </div>
  );
}

function AllocationRow({ alloc, onApprove, acting }: {
  alloc: WorkerAllocationResponse;
  onApprove?: (id: string) => void;
  acting: boolean;
}) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 80px 70px 70px 70px auto',
      gap: 10, padding: '10px 14px',
      background: alloc.status === 'proposed' ? 'rgba(251,191,36,0.05)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${alloc.status === 'proposed' ? 'rgba(251,191,36,0.3)' : 'var(--omega-rim, rgba(255,255,255,0.08))'}`,
      borderRadius: 4,
    }}>
      <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {alloc.company_id.slice(0, 18)}
      </span>
      <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: deltaColor(alloc.worker_delta), fontWeight: 700 }}>
        {fmtDelta(alloc.worker_delta)}
      </span>
      <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)' }}>{alloc.total_after}</span>
      <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>{fmtTs(alloc.allocated_at)}</span>
      <span style={{
        fontSize: '7.5pt', fontFamily: 'var(--omega-mono)', textTransform: 'uppercase',
        color: alloc.status === 'approved' ? '#34d399' : '#fbbf24',
      }}>
        {alloc.status}
      </span>
      {onApprove && alloc.status === 'proposed' ? (
        <button
          disabled={acting}
          onClick={() => onApprove(alloc.allocation_id)}
          style={{
            background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.4)',
            borderRadius: 3, color: '#34d399', fontFamily: 'var(--omega-mono)',
            fontSize: '7.5pt', fontWeight: 600, padding: '3px 8px',
            cursor: acting ? 'default' : 'pointer', opacity: acting ? 0.4 : 1,
          }}
        >
          Approve
        </button>
      ) : <span />}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaWorkerAllocationPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const queryClient = useQueryClient();
  const [acting, setActing] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const { data: approved } = useQuery({
    queryKey: ['alpha-omega', 'worker-allocation', 'approved'],
    queryFn: () => apiFetch<WorkerAllocationsResponse>('/alpha-omega/worker-allocation'),
    refetchInterval: 30_000,
  });

  const { data: proposed } = useQuery({
    queryKey: ['alpha-omega', 'worker-allocation', 'proposed'],
    queryFn: () => apiFetch<WorkerAllocationsResponse>('/alpha-omega/worker-allocation/proposed'),
    refetchInterval: 30_000,
  });

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleApprove(allocationId: string) {
    setActing(true);
    try {
      await apiFetch<ActionResponse>('/alpha-omega/worker-allocation/approve', {
        method: 'POST',
        body: { allocation_id: allocationId },
      });
      await queryClient.invalidateQueries({ queryKey: ['alpha-omega', 'worker-allocation'] });
      showToast('Allocation approved', true);
    } catch {
      showToast('Error — try again', false);
    } finally {
      setActing(false);
    }
  }

  const pendingCount = proposed?.total ?? 0;

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',       to: '/omega' },
        { label: 'Alpha-Omega', to: '/omega/alpha-omega' },
        { label: 'Workers' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Worker Allocation
        </h1>
        {pendingCount > 0 && (
          <span style={{
            fontSize: '7.5pt', padding: '2px 8px',
            border: '1px solid rgba(251,191,36,0.5)', color: '#fbbf24',
            letterSpacing: '0.06em', fontWeight: 700, borderRadius: 3,
          }}>
            {pendingCount} Pending approval
          </span>
        )}
      </div>

      {/* Proposed allocations — action required */}
      {proposed && proposed.allocations.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fbbf24', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
            Pending approval ({proposed.total})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 860 }}>
            {proposed.allocations.map(a => (
              <AllocationRow key={a.allocation_id} alloc={a} onApprove={handleApprove} acting={acting} />
            ))}
          </div>
        </section>
      )}

      {/* Approved allocations — history */}
      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
          Approved allocations ({approved?.total ?? '—'})
        </div>
        {approved && approved.allocations.length === 0 && (
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>No allocations.</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 860 }}>
          {approved?.allocations.map(a => (
            <AllocationRow key={a.allocation_id} alloc={a} acting={acting} />
          ))}
        </div>
      </section>

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </div>
  );
}
